import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Mail, Search, ChevronDown, ChevronRight, Truck, RotateCcw, ShieldCheck, CreditCard, Package, HelpCircle } from "lucide-react";

const CATEGORIES = [
  { icon: Truck, title: "Shipping & Delivery", href: "#faqs", desc: "Track orders, delivery times, and pincode coverage." },
  { icon: RotateCcw, title: "Returns & Refunds", href: "#faqs", desc: "Easy 7-day returns, replacement policy." },
  { icon: CreditCard, title: "Payment & Billing", href: "#faqs", desc: "GST invoices, refunds and cash on delivery." },
  { icon: Package, title: "Order Issues", href: "#faqs", desc: "Missing items, damaged packages, wrong products." },
  { icon: ShieldCheck, title: "Product Authenticity", href: "#faqs", desc: "Genuine sourcing, warranty and certifications." },
  { icon: HelpCircle, title: "Account & Login", href: "#faqs", desc: "Password reset, profile, and phone verification." },
];

const FAQS = [
  { q: "How long does delivery take?", a: "Most orders are delivered in 3–5 business days across India. Metro cities usually get 2-day dispatch. Refurbished equipment ships within 7-10 days including quality checks." },
  { q: "Are all products 100% genuine?", a: "Yes. Every product on Dyntra Store is sourced directly from authorized distributors or the brand itself. Every equipment ships with a manufacturer warranty and GST invoice." },
  { q: "What is your return policy?", a: "Consumables and PPE cannot be returned once opened for hygiene reasons. Equipment and instruments are covered under a 7-day easy-return policy if unused and in original packaging." },
  { q: "Do I get a GST invoice?", a: "Yes — a GST-compliant invoice is emailed to you within 24 hours of order confirmation and is downloadable from your Orders page." },
  { q: "How does Dr. Dyntra AI work?", a: "Dr. Dyntra is our AI dental assistant powered by Claude Sonnet 4.5. Ask about products, procedures or case-based recommendations — for educational guidance only. Always use clinical judgement for patient-specific decisions." },
  { q: "How do I become a Dyntra Partner?", a: "Head to the 'Become a Dyntra Partner' section on the homepage. You'll get exclusive benefits, marketing support and higher product visibility." },
  { q: "Do you accept cash on delivery?", a: "Yes, COD is available on orders under ₹50,000 in most pincodes. Larger orders require prepayment via UPI, bank transfer or card." },
  { q: "Can I cancel an order?", a: "Yes, until it is dispatched. Head to Orders → select the order → Cancel. Refunds process within 5-7 business days." },
];

export default function Support() {
  const [openIdx, setOpenIdx] = useState(0);
  const [query, setQuery] = useState("");
  const filtered = FAQS.filter((f) => (f.q + f.a).toLowerCase().includes(query.toLowerCase()));

  return (
    <main className="pb-16">
      {/* Hero */}
      <section className="dyntra-container mt-6">
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, #361D82 0%, #4B2AAD 55%, #6B4CD9 100%)" }}>
          <div className="relative px-8 sm:px-14 py-14 md:py-16 text-white text-center max-w-3xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/80 mb-3">Support Center</p>
            <h1 data-testid="support-title" className="font-display text-4xl sm:text-5xl font-extrabold">How can we help?</h1>
            <p className="mt-3 text-white/90 text-base max-w-xl mx-auto">Search our help center, browse FAQs, or reach us directly — we typically respond within 2 hours.</p>
            <div className="mt-6 relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                data-testid="support-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs (e.g. delivery, GST, refund)"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm text-gray-900 bg-white outline-none focus:ring-4 focus:ring-white/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="dyntra-container mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ContactCard testid="contact-chat" icon={MessageCircle} title="Live Chat" desc="Chat with a dentist-support specialist" cta="Start Chat" color="bg-[#F3EFFC] text-[#4B2AAD]" />
          <ContactCard testid="contact-whatsapp" icon={MessageCircle} title="WhatsApp" desc="+91 99999 00000 · 9AM–9PM IST" cta="Message Us" color="bg-emerald-50 text-emerald-700" />
          <ContactCard testid="contact-call" icon={Phone} title="Call Support" desc="1800-DYNTRA (India Toll-Free)" cta="Call Now" color="bg-[#FFF7E0] text-[#8A5A00]" />
        </div>
      </section>

      {/* Browse Categories */}
      <section className="dyntra-container mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Browse Help Topics</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((c) => (
            <a href={c.href} key={c.title} data-testid={`help-cat-${c.title.replace(/\s+/g,"-").toLowerCase()}`} className="dyntra-card p-5 group">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#F3EFFC] flex items-center justify-center shrink-0"><c.icon className="h-5 w-5 text-[#4B2AAD]" /></div>
                <div className="flex-1">
                  <h3 className="font-display text-base font-bold text-gray-900 group-hover:text-[#4B2AAD] transition-colors">{c.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{c.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-[#4B2AAD] transition-colors" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="dyntra-container mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
            <p className="text-sm text-gray-500 mt-2">Can't find what you need? <Link to="/contact" className="text-[#4B2AAD] font-semibold hover:underline">Contact us</Link>.</p>
            <div className="mt-5 dyntra-card p-5 hover:translate-y-0 bg-[#F3EFFC] border-[#4B2AAD]/15">
              <Mail className="h-5 w-5 text-[#4B2AAD] mb-2" />
              <p className="font-display font-bold text-sm text-gray-900">Prefer email?</p>
              <a href="mailto:support@dyntra.com" className="text-sm text-[#4B2AAD] font-semibold">support@dyntra.com</a>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-2">
            {filtered.length === 0 && <p className="text-sm text-gray-500 py-8 text-center">No FAQ matches. Try a different keyword.</p>}
            {filtered.map((f, i) => {
              const open = openIdx === i;
              return (
                <div key={i} data-testid={`faq-${i}`} className="dyntra-card p-0 hover:translate-y-0 hover:shadow-none overflow-hidden">
                  <button
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-sm text-gray-900 pr-4">{f.q}</span>
                    <ChevronDown className={`h-4 w-4 text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactCard({ icon: Icon, title, desc, cta, color, testid }) {
  return (
    <div data-testid={testid} className="dyntra-card p-6 flex items-center gap-4">
      <div className={`h-14 w-14 rounded-2xl ${color} flex items-center justify-center shrink-0`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <h3 className="font-display text-base font-bold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
        <button className="text-xs font-semibold text-[#4B2AAD] mt-1.5 hover:underline">{cta} →</button>
      </div>
    </div>
  );
}
